import React, { FC, memo, useEffect } from 'react'
import { connect } from 'react-redux'
import isEqual from 'lodash/isEqual'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { Grid, Typography } from 'transfix-ui/core'
import {
  fetchChildUsers,
  showDeleteUserDialog,
  resendInvite,
  approveParentUser,
  rejectParentUser,
  setUndeleteUserId,
  undeleteUser,
  clearUndelete,
  confirmParentUser,
} from 'utils/actions/user'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { capitalize } from 'utils/capitalize'
import { formatDate, formatActivityDateTime } from 'utils/formatDatetime'
import { isUserType } from 'utils/userTypes'
import { USER_TYPE } from 'constants/userTypes'
import { useUserProfileHeaderActions } from './hooks/useUserProfileHeaderActions'

interface IUserProfileHeaderProps {
  id: number
  applicationId?: number
  email?: string
  userType: USER_TYPE
  userTypes: string[]
  userTypeName: string
  stateName: string
  firstName: string
  lastName: string
  lastSignInAt?: string
  createdAt: string
  confirmedAt?: string
  confirmParentUser: (id: number) => void
  approved: boolean
  rejected: boolean
  showActivation: boolean
  showUserAccounts: boolean
  actionInProgress: boolean
  resendInvite: (email?: string) => void
  showDeleteUserDialog: (id: number) => void
  showImpersonateUserDialog: (id: number) => void
  approveParentUser: (id: number) => void
  rejectParentUser: (id: number) => void
  impersonateParentUser: (id: number) => void
  isAdmin: boolean
  childAdminUserId: number
  clearUndelete: () => void
  setUndeleteUserId: (id: number) => void
  undeleteUser: (id: number) => void
  undeleted: {
    id: number | null
    loading: boolean
    error: string
    success?: boolean
  }
}

export const UserProfileHeaderComponent: FC<IUserProfileHeaderProps> = memo(
  ({
    actionInProgress,
    applicationId,
    approveParentUser,
    approved,
    childAdminUserId,
    clearUndelete,
    confirmParentUser,
    confirmedAt,
    createdAt,
    email,
    firstName,
    id,
    isAdmin,
    lastName,
    lastSignInAt,
    rejectParentUser,
    rejected,
    resendInvite,
    setUndeleteUserId,
    showActivation,
    showDeleteUserDialog,
    showImpersonateUserDialog,
    showUserAccounts,
    stateName,
    undeleteUser,
    undeleted,
    userType,
    userTypeName,
    userTypes,
  }) => {
    const { openToast } = useGlobalToast()
    const { userDetails, userPermissions } = useUserDetails()
    const { loading, sendPasswordLink } = useUserProfileHeaderActions({
      email,
      userType,
    })

    const {
      canApproveUsers: canApprove,
      canImpersonate: hasImpersonatePermission,
      navCarriers,
      navShippers,
      usersDelete: canDelete,
      usersUndelete: canUndelete,
    } = userPermissions || {}
    const {
      error: undeleteError,
      id: undeleteId,
      loading: undeleteLoading,
      success: undeleteSuccess,
    } = undeleted || {}
    const { email: currentUserEmail, userType: currentUserType = '' } = userDetails || {}
    const currentUserIsAdmin = isUserType('admin', currentUserType)
    const isConfirmed = !!confirmedAt
    const canImpersonate =
      hasImpersonatePermission &&
      (isConfirmed || approved) &&
      stateName === 'Approved' &&
      email !== currentUserEmail &&
      !['system', 'driver'].includes(userType)
    const companyVisible =
      !['admin', 'bot'].includes(userType) && applicationId && navShippers && navCarriers

    const resendUserInviteEmailHandler = () => resendInvite(email)
    const deleteUserHandler = () => {
      showDeleteUserDialog(id)
      clearUndelete()
    }
    const approveUserHandler = () => approveParentUser(id) // note: for shipper users
    const rejectUserHandler = () => rejectParentUser(id) // note: for shipper users
    const confirmUserHandler = () => confirmParentUser(id)
    const impersonateUserHandler = () => showImpersonateUserDialog(id)
    const undeleteAdminUserHandler = () => {
      if (childAdminUserId) {
        setUndeleteUserId(childAdminUserId)
        undeleteUser(childAdminUserId)
      }
    }

    const userCanOnboard = showActivation && companyVisible
    // We hide the password reset buttons for users who are _only_ drivers,
    // since driver users do not have passwords.
    // We push them a verification code instead.
    const isOnlyDriver = isEqual(userTypes, ['driver'])
    const passwordManagementVisible = !isOnlyDriver && currentUserIsAdmin
    const showSecurityRecovery = !userCanOnboard && passwordManagementVisible && lastSignInAt
    const showResendInvite = !lastSignInAt || (userCanOnboard && passwordManagementVisible && email)
    const isDeleted = stateName === 'Deleted' && !undeleteSuccess
    const showDeleteButton = !isDeleted && canDelete
    const showRejectApproveButton = canApprove && !!applicationId && !(approved || rejected)
    const showUndeleteButton = isAdmin && currentUserIsAdmin && isDeleted && canUndelete
    const showConfirmAccountButton = canApprove && !isConfirmed

    useEffect(() => {
      if (undeleteId && !undeleteLoading && undeleteId === childAdminUserId) {
        const toastMessage = undeleteSuccess ? 'User is undeleted' : undeleteError
        const toastType = undeleteSuccess ? 'default' : 'alert'
        openToast({ toastMessage, toastType })
      }
    }, [undeleteSuccess, undeleteError, openToast, undeleteId, childAdminUserId, undeleteLoading])

    const disableActionButtons = actionInProgress || loading

    return (
      <Grid container xs={12}>
        <Grid container direction='column' item md={5} xs={12}>
          <Grid item>
            <Typography variant='h2' data-test='user-name'>
              {firstName} {lastName}
            </Typography>
          </Grid>

          <Grid item>
            <p data-test='user-meta'>
              {(userTypeName || userType) && !showUserAccounts && (
                <span>{userTypeName || capitalize(userType)}&nbsp;&middot;&nbsp;</span>
              )}
              {lastSignInAt && (
                <span>Last log in {formatActivityDateTime(lastSignInAt)}&nbsp;&nbsp;</span>
              )}
              <span className='text-secondary' data-test='user-since'>
                User since {formatDate(createdAt, 'dateFormat')}
              </span>
            </p>
          </Grid>
        </Grid>

        <Grid container item md={7} xs={12} justifyContent='flex-end' spacing={1}>
          {showSecurityRecovery && (
            <Grid item>
              <Button
                disabled={disableActionButtons}
                onClick={() => sendPasswordLink('login_link')}>
                Send Login Link
              </Button>
            </Grid>
          )}
          {showResendInvite && (
            <Grid item>
              <Button disabled={disableActionButtons} onClick={resendUserInviteEmailHandler}>
                Resend Invite Email
              </Button>
            </Grid>
          )}
          {showSecurityRecovery && (
            <Grid item>
              <Button
                disabled={disableActionButtons}
                onClick={() => sendPasswordLink('reset_password')}>
                Reset Password
              </Button>
            </Grid>
          )}
          {showDeleteButton && (
            <Grid item>
              <Button disabled={disableActionButtons} onClick={deleteUserHandler}>
                Delete User
              </Button>
            </Grid>
          )}
          {showUndeleteButton && (
            <Grid item>
              <Button disabled={disableActionButtons} onClick={undeleteAdminUserHandler}>
                Undelete User
              </Button>
            </Grid>
          )}
          {!showUserAccounts && showRejectApproveButton && (
            <Grid item>
              <Button disabled={disableActionButtons} onClick={rejectUserHandler}>
                Reject User
              </Button>
            </Grid>
          )}
          {!showUserAccounts && showRejectApproveButton && (
            <Grid item>
              <Button disabled={disableActionButtons} onClick={approveUserHandler}>
                Approve User
              </Button>
            </Grid>
          )}
          {showConfirmAccountButton && (
            <Grid item>
              <Button disabled={actionInProgress} onClick={confirmUserHandler}>
                Confirm User
              </Button>
            </Grid>
          )}
          {canImpersonate && (
            <Grid item>
              <Button
                disabled={disableActionButtons}
                onClick={impersonateUserHandler}
                data-test='impersonationButton'>
                Impersonate User
              </Button>
            </Grid>
          )}
        </Grid>

        {isDeleted && (
          <Grid container item xs={12}>
            <InlineMessage
              message='This account has been deleted'
              type={INLINE_MESSAGE_VARIANTS.WARNING}
            />
          </Grid>
        )}
      </Grid>
    )
  }
)

export const UserProfileHeader = connect(
  ({
    userProfile: {
      actionInProgress,
      isAdmin,
      isDispatcher,
      isDriver,
      isOwnerOperator,
      undeleted,
      userProfileDetails,
    } = {} as any,
    childUsers: { users: [user] = [] } = {},
  }: any) => {
    return {
      ...userProfileDetails,
      childAdminUserId: user?.id || null,
      isAdmin,
      undeleted,
      actionInProgress,
      showUserAccounts: isDriver || isDispatcher || isOwnerOperator,
    }
  },
  {
    resendInvite,
    showDeleteUserDialog,
    approveParentUser,
    rejectParentUser,
    setUndeleteUserId,
    undeleteUser,
    fetchChildUsers,
    clearUndelete,
    confirmParentUser,
  }
)(UserProfileHeaderComponent)
